import { UploadOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Modal,
  Segmented,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd'
import { SIGNOFF_ROLE_LABELS } from 'pages/reporting/shared/approval/approvalRules'
import { SignaturePad } from 'pages/reporting/shared/components/SignaturePad'
import { TypedSignatureFields } from 'pages/reporting/shared/eSignature/components/TypedSignatureFields'
import { SignRequest } from 'pages/reporting/shared/eSignature/utils/signReport'
import {
  getTypedSignatureFont,
  renderTypedSignatureDataUrl,
  TYPED_SIGNATURE_FONTS,
  TypedSignatureFont,
} from 'pages/reporting/shared/eSignature/utils/typedSignature'
import { FC, useState } from 'react'
import styled from 'styled-components'
import { ESignatureMethod, SignoffRole } from 'types'

const { Text } = Typography

const QualificationsSelect = styled(Select<string>)`
  width: 100%;
`

const CapturedPreview = styled.img`
  max-height: 6rem;
  max-width: 100%;
  border: 1px solid ${(props) => props.theme.forecast.border};
  border-radius: 0.5rem;
  background: white;
  padding: 0.5rem;
`

const METHOD_OPTIONS: { label: string; value: ESignatureMethod }[] = [
  { label: 'Type', value: 'typed' },
  { label: 'Draw', value: 'drawn' },
  { label: 'Upload', value: 'uploaded' },
]

const readFileAsDataUrl = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      typeof reader.result === 'string'
        ? resolve(reader.result)
        : reject(new Error('Could not read the image'))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

interface ESignatureDialogProps {
  role: SignoffRole
  signerName: string
  defaultQualifications: string | undefined
  qualificationOptions: readonly string[]
  onCancel: () => void
  onSign: (request: SignRequest) => Promise<void>
}

export const ESignatureDialog: FC<ESignatureDialogProps> = ({
  role,
  signerName,
  defaultQualifications,
  qualificationOptions,
  onCancel,
  onSign,
}) => {
  const [method, setMethod] = useState<ESignatureMethod>('typed')
  const [typedText, setTypedText] = useState(signerName)
  const [font, setFont] = useState<TypedSignatureFont>(TYPED_SIGNATURE_FONTS[0])
  const [capturedDataUrl, setCapturedDataUrl] = useState<string>()
  const [qualifications, setQualifications] = useState(defaultQualifications)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string>()

  const hasSignature =
    method === 'typed' ? typedText.trim().length > 0 : !!capturedDataUrl
  const qualificationSelectOptions = qualificationOptions.map((value) => ({
    value,
    label: value,
  }))

  const handleMethodChange = (next: ESignatureMethod) => {
    setMethod(next)
    setCapturedDataUrl(undefined)
  }

  const handleUpload = (file: Blob) => {
    readFileAsDataUrl(file)
      .then(setCapturedDataUrl)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Could not read the image')
      )
    return false
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError(undefined)
    try {
      const signatureDataUrl =
        method === 'typed'
          ? await renderTypedSignatureDataUrl(typedText.trim(), font)
          : capturedDataUrl
      if (!signatureDataUrl) throw new Error('Provide a signature first')
      await onSign({
        role,
        method,
        signatureDataUrl,
        signatureText: method === 'typed' ? typedText.trim() : undefined,
        typedFontId: method === 'typed' ? font.id : undefined,
        qualifications,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to sign the report')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      open
      title={`Sign as ${SIGNOFF_ROLE_LABELS[role]}`}
      onCancel={onCancel}
      footer={
        <Space>
          <Button disabled={isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type='primary'
            loading={isSubmitting}
            disabled={!hasSignature}
            onClick={handleSubmit}>
            Sign report
          </Button>
        </Space>
      }>
      <Space orientation='vertical' size='middle' style={{ width: '100%' }}>
        <Text type='secondary'>
          Signing as {signerName}. Clicking Sign report applies your electronic
          signature to the current draft PDF.
        </Text>

        <Segmented<ESignatureMethod>
          block
          options={METHOD_OPTIONS}
          value={method}
          onChange={handleMethodChange}
        />

        {method === 'typed' && (
          <TypedSignatureFields
            text={typedText}
            font={font}
            onTextChange={setTypedText}
            onFontChange={(id) => setFont(getTypedSignatureFont(id))}
          />
        )}
        {method === 'drawn' && (
          <SignaturePad
            onDrawingChange={setCapturedDataUrl}
            disabled={isSubmitting}
          />
        )}
        {method === 'uploaded' && (
          <>
            <Upload
              accept='image/png,image/jpeg'
              showUploadList={false}
              beforeUpload={handleUpload}>
              <Button icon={<UploadOutlined />}>Choose PNG or JPEG</Button>
            </Upload>
            {capturedDataUrl && (
              <CapturedPreview src={capturedDataUrl} alt='Uploaded signature' />
            )}
          </>
        )}

        <QualificationsSelect
          allowClear
          placeholder='Qualifications (optional)'
          options={qualificationSelectOptions}
          value={qualifications}
          onChange={setQualifications}
        />

        {error && <Alert type='error' showIcon message={error} />}
      </Space>
    </Modal>
  )
}
